// 解說裡的「(A)、選 (B)、C 項」等字母指稱，會因選項顯示時隨機排列而對不上畫面。
// 顯示解說時，把這類指稱換成該選項的完整文字（字母以題庫儲存的原始順序為準）。
// 只處理沒有歧義的寫法：括號字母、「選 X」、「選項 X」、「X 項」；單獨的「A 為…」「C、D」可能是題目裡的點或變數，保持原樣。

const L = 'ABCD';
const q = (t: string) => `「${t}」`;
// 選項文字本身就是「圖 (A)」「(B)」這類字母標示時不替換，避免變成「圖 (A)」重複
const isLabelOnly = (t: string) => /^\s*(圖|選項)?\s*[（(]?[A-Da-d][）)]?\s*$/.test(t);

// 作答時選項是否會被隨機排列：歷屆考題（有 year）與選項只是代號（A–D、甲乙丙丁，每個選項 1 字以內）時保留原順序
export const optionsAreShuffled = (q: { year?: number | null; o?: unknown }): boolean =>
  !(q.year != null || !Array.isArray(q.o) || q.o.every(x => String(x ?? '').trim().length <= 1));

// 顯示用：選項會被洗牌的題目才把字母換成選項文字；保留原順序的題目（歷屆考題、看圖題）字母與畫面一致，維持原文
export const explanationOf = (q: { e?: string; o?: unknown; year?: number | null }): string =>
  optionsAreShuffled(q) ? resolveOptionRefs(q.e, q.o) : (q.e ?? '');

export function resolveOptionRefs(e: string | undefined, o: unknown): string {
  const text = e ?? '';
  if (!text || !Array.isArray(o) || o.length < 2) return text;
  const opt = (letter: string): string | null => {
    const i = L.indexOf(letter.toUpperCase());
    const t = i >= 0 && i < o.length ? String(o[i] ?? '').trim() : '';
    return t && !isLabelOnly(t) && !/[（(][A-Da-d][）)]/.test(t) ? t : null;
  };
  return text
    // 選 (B)、選項 (B)、選項 B → 選「…」
    .replace(/(選項|選)\s*[（(]([A-Da-d])[）)]/g, (m, w: string, l: string) => { const t = opt(l); return t ? `${w === '選' ? '選' : ''}${q(t)}` : m; })
    .replace(/選項\s*([A-D])(?![A-Za-z])\s*/g,(m, l: string) => { const t = opt(l); return t ? q(t) : m; })
    // 圖 (B) → 「…」：選項是文字描述（沒有自己的代號）時，「圖 (B)」其實是在指第 B 個選項
    .replace(/圖\s*[（(]([A-Da-d])[）)]/g, (m, l: string) => { const t = opt(l); return t ? q(t) : m; })
    // A選項 → 「…」
    .replace(/(^|[^A-Za-z])([A-D])\s*選項/g, (m, pre: string, l: string) => { const t = opt(l); return t ? `${pre}${q(t)}` : m; })
    // (B) → 「…」
    .replace(/[（(]([A-Da-d])[）)]/g, (m, l: string, at: number, whole: string) => {
      const t = opt(l);
      // 「圖 (A)」「P(A)」是看圖或數學符號，不是選項指稱（不用 lookbehind，舊版 Safari 不支援）
      const before = whole.slice(Math.max(0, at - 3), at);
      return t && !/[A-Za-z0-9]$/.test(before) && !/圖\s?$/.test(before) ? q(t) : m;
    })
    // B 項 → 「…」（前一個字不是英文字母）
    .replace(/(^|[^A-Za-z])([A-D])\s*項/g, (m, pre: string, l: string) => { const t = opt(l); return t ? `${pre}${q(t)}` : m; });
}
